import React from 'react';
import { Navbar } from '../components/Navbar';
import { CaseStudyHero } from '../components/CaseStudyHero';
import { CaseStudySection } from '../components/CaseStudySection';
import { ContentNavigation } from '../components/ContentNavigation';
import { ImpactEffortMatrix } from '../components/ImpactEffortMatrix';
import { DesignShowcase } from '../components/DesignShowcase';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Layers, Database, ShoppingCart, BarChart3 } from 'lucide-react';
export function VerizonCaseStudy() {
  const navLinks = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Work',
    href: '/#work'
  }, {
    label: 'About',
    href: '/aboutme'
  }];
  return <div className="min-h-screen bg-black text-white selection:bg-white selection:text-black font-sans" data-id="element-537">
      <Navbar brand="Taylor Sams" links={navLinks} contactEmail="taylorjsams@gmail.com" data-id="element-538" />

      <main data-id="element-539">
        <CaseStudyHero title="Verizon BUSS 2.0" description="Imagining an innovative & robust sales tool for incredible bulk volume quote-building and catalog management" role="Product Designer" duration="4 Weeks" tools={['Sketch', 'Adobe Creative Suite', 'Full Story']} industry="Telecoms" tags={['Telecommunications', 'Web App', 'UX/UI Design']} heroImage="https://i.imgur.com/lUcIAKw.png" data-id="element-540" />

        <div className="flex gap-12 max-w-7xl mx-auto px-6" data-id="element-541">
          <ContentNavigation data-id="element-542" />

          <div className="flex-1 min-w-0" data-id="element-543">
            {/* Project Overview */}
            <CaseStudySection id="overview" darker data-id="element-544">
              <div className="grid md:grid-cols-2 gap-16 items-start" data-id="element-545">
                <div data-id="element-546">
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-547">
                    About the Project
                  </h3>
                  <div className="space-y-6 text-gray-400 text-lg leading-relaxed" data-id="element-548">
                    <p data-id="element-549">
                      Verizon has a nationally recognizable brand and an
                      incredible marketing team - they've always been pretty
                      capable of designing their own products and experiences.
                      However, they're very aware that data-intense or complex
                      software experiences are just not their forté. In addition
                      to that they have no internal resources to conduct
                      unbiased research.
                    </p>
                    <p data-id="element-550">
                      This was the motivation behind our first engagement
                      together - their internal sales tools have become a
                      terribly inefficient mess and all attempts to design a
                      solution have failed to yield any successful results. We
                      don't have to design the whole thing today, but if we can
                      paint an attractive picture of what this product might
                      look like and give its stakeholders adequate research to
                      justify building it, then we will have been successful in
                      cementing our role as their "guaranteed success" strategic
                      design partner.
                    </p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-2xl p-8 border border-white/10 backdrop-blur-sm" data-id="element-551">
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-552">
                    The Challenge ⚡️
                  </h3>
                  <p className="text-xl text-gray-300 font-light italic mb-8" data-id="element-553">
                    "Sales reps are drowning in spreadsheets and manual
                    processes. Building bulk quotes takes hours, catalog
                    management is chaotic, and errors cost us millions
                    annually."
                  </p>
                  <div className="space-y-4" data-id="element-554">
                    <div className="flex items-center gap-4 text-gray-400" data-id="element-555">
                      <div className="p-2 bg-red-500/10 rounded-full text-red-500" data-id="element-556">
                        <ArrowRight className="w-5 h-5 rotate-45" data-id="element-557" />
                      </div>
                      <span data-id="element-558">
                        Manual quote building taking 4-6 hours per order
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-400" data-id="element-559">
                      <div className="p-2 bg-red-500/10 rounded-full text-red-500" data-id="element-560">
                        <ArrowRight className="w-5 h-5 rotate-45" data-id="element-561" />
                      </div>
                      <span data-id="element-562">Catalog inconsistencies across regions</span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-400" data-id="element-563">
                      <div className="p-2 bg-red-500/10 rounded-full text-red-500" data-id="element-564">
                        <ArrowRight className="w-5 h-5 rotate-45" data-id="element-565" />
                      </div>
                      <span data-id="element-566">High error rate in bulk volume pricing</span>
                    </div>
                  </div>
                </div>
              </div>
            </CaseStudySection>

            {/* Research & Discovery */}
            <CaseStudySection id="research" title="Research & Discovery" data-id="element-567">
              <div className="mb-16" data-id="element-568">
                <p className="text-xl text-gray-300 leading-relaxed max-w-4xl" data-id="element-569">
                  There wasn't a clear problem statement from the client, just
                  that there were big structural issues. So stakeholder
                  interviews seemed like a great place to start to gather
                  information directly from those most impacted. We conducted 14
                  interview sessions with more than 20 stakeholders and
                  identified 12 types of users. These were then distilled down
                  into 3 personas who represented clear user paths we could
                  design for.
                </p>
              </div>

              {/* Research Artifacts */}
              <div className="space-y-8" data-id="element-570">
                <div className="bg-zinc-700 rounded-3xl overflow-hidden border border-white/10" data-id="element-571">
                  <div className="p-6" data-id="element-572">
                    <img src="https://cdn.magicpatterns.com/uploads/5tE7qygG84o3qArP7Pusit/interviewnotes.png" alt="Stakeholder Interview Notes and Affinity Mapping" className="w-full h-auto" data-id="element-573" />
                  </div>
                  <div className="p-6 bg-black/40" data-id="element-574">
                    <p className="text-sm text-gray-400 text-center" data-id="element-575">
                      Affinity mapping from 14 stakeholder interview sessions
                      across 12 user types
                    </p>
                  </div>
                </div>

                <div className="bg-zinc-100 rounded-3xl overflow-hidden border border-black/70" data-id="element-576">
                  <img src="https://cdn.magicpatterns.com/uploads/68GyqmMWuGNbYLGrCf1cny/Interview02.png" alt="Interview Matrix and User Type Analysis" className="w-full h-auto" data-id="element-577" />
                  <div className="p-6 bg-black/80" data-id="element-578">
                    <p className="text-sm text-gray-400 text-center" data-id="element-579">
                      Interview matrix mapping stakeholder roles to key
                      functions and pain points
                    </p>
                  </div>
                </div>

                <div className="bg-zinc-100 rounded-3xl overflow-hidden border border-black/70" data-id="element-580">
                  <img src="https://cdn.magicpatterns.com/uploads/qwgUMz5rPmw8XFqdQKPgve/eco.png" alt="User Ecosystem and Workflow Diagram" className="w-full h-auto" data-id="element-581" />
                  <div className="p-6 bg-black/80" data-id="element-582">
                    <p className="text-sm text-gray-400 text-center" data-id="element-583">
                      User ecosystem showing the relationships between personas
                      and key workflows
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Findings */}
              <div className="grid md:grid-cols-3 gap-8 mt-16" data-id="element-584">
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-585">
                  <div className="text-3xl font-bold text-white mb-2" data-id="element-586">14</div>
                  <h4 className="text-lg font-bold mb-2" data-id="element-587">Interview Sessions</h4>
                  <p className="text-gray-400 text-sm" data-id="element-588">
                    In-depth conversations with stakeholders across the
                    organization
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-589">
                  <div className="text-3xl font-bold text-white mb-2" data-id="element-590">12</div>
                  <h4 className="text-lg font-bold mb-2" data-id="element-591">
                    User Types Identified
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-592">
                    Diverse roles from sales reps to product managers
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-593">
                  <div className="text-3xl font-bold text-white mb-2" data-id="element-594">3</div>
                  <h4 className="text-lg font-bold mb-2" data-id="element-595">Core Personas</h4>
                  <p className="text-gray-400 text-sm" data-id="element-596">
                    Distilled into clear user paths we could design for
                  </p>
                </div>
              </div>
            </CaseStudySection>

            {/* Conceptualization */}
            <CaseStudySection id="conceptualization" title="Conceptualization & Strategy" darker data-id="element-597">
              {/* Three Core Personas */}
              <div className="mb-20" data-id="element-598">
                <h3 className="text-2xl font-bold text-white mb-10" data-id="element-599">
                  Core User Personas
                </h3>
                <div className="grid md:grid-cols-3 gap-8" data-id="element-600">
                  {/* Persona A - Francis */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-601">
                    <div className="flex items-center gap-4 mb-6" data-id="element-602">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center overflow-hidden" data-id="element-603">
                        <img className="h-full w-full rounded-full object-cover shadow-sm" src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=200" alt="User avatar" data-id="element-604" />
                      </div>
                      <div data-id="element-605">
                        <h4 className="text-xl font-bold text-white" data-id="element-606">
                          Francis
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-607">Sales / B2B</p>
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-608">
                      <h5 className="text-sm font-semibold text-white mb-2 uppercase tracking-wider" data-id="element-609">
                        Problem Statement
                      </h5>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-610">
                        Francis is a sales executive specializing in B2B sales.
                        He wants to build stronger relationships with Verizon
                        customers. Francis wants to get to know customers more
                        and see what sticks.
                      </p>
                    </div>
                    <div data-id="element-611">
                      <h5 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider" data-id="element-612">
                        Challenges
                      </h5>
                      <ul className="space-y-2 text-sm text-gray-400" data-id="element-613">
                        <li className="flex items-start gap-2" data-id="element-614">
                          <span className="text-red-400 mt-1" data-id="element-615">•</span>
                          <span data-id="element-616">
                            Partner content is outdated because it's manually
                            updated
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-617">
                          <span className="text-red-400 mt-1" data-id="element-618">•</span>
                          <span data-id="element-619">
                            Sales enablement is a single BuSS homepage which is
                            difficult to locate
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-620">
                          <span className="text-red-400 mt-1" data-id="element-621">•</span>
                          <span data-id="element-622">BuSS isn't compatible with MyBiz instance</span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-623">
                          <span className="text-red-400 mt-1" data-id="element-624">•</span>
                          <span data-id="element-625">
                            Outdated SKUs that aren't useful or sellable anymore
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Persona B - Shae */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-626">
                    <div className="flex items-center gap-4 mb-6" data-id="element-627">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center overflow-hidden" data-id="element-628">
                        <img className="h-full w-full rounded-full object-cover shadow-sm" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200" alt="User avatar" data-id="element-629" />
                      </div>
                      <div data-id="element-630">
                        <h4 className="text-xl font-bold text-white" data-id="element-631">Shae</h4>
                        <p className="text-sm text-gray-400" data-id="element-632">
                          Product / Business Solutions
                        </p>
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-633">
                      <h5 className="text-sm font-semibold text-white mb-2 uppercase tracking-wider" data-id="element-634">
                        Problem Statement
                      </h5>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-635">
                        Shae is a Solutions Designer for small to midsize
                        business products. She wants a better way to find
                        products and manage the solutions she's creating.
                      </p>
                    </div>
                    <div data-id="element-636">
                      <h5 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider" data-id="element-637">
                        Challenges
                      </h5>
                      <ul className="space-y-2 text-sm text-gray-400" data-id="element-638">
                        <li className="flex items-start gap-2" data-id="element-639">
                          <span className="text-red-400 mt-1" data-id="element-640">•</span>
                          <span data-id="element-641">
                            Finding products across multiple BuSS catalogs
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-642">
                          <span className="text-red-400 mt-1" data-id="element-643">•</span>
                          <span data-id="element-644">
                            No good way to customize bundles to meet changing
                            needs
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-645">
                          <span className="text-red-400 mt-1" data-id="element-646">•</span>
                          <span data-id="element-647">
                            BuSS solutions aren't searchable or indexed well
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Persona C - Carmen */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-648">
                    <div className="flex items-center gap-4 mb-6" data-id="element-649">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center overflow-hidden" data-id="element-650">
                        <img className="h-full w-full rounded-full object-cover shadow-sm" src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200" alt="User avatar" data-id="element-651" />
                      </div>
                      <div data-id="element-652">
                        <h4 className="text-xl font-bold text-white" data-id="element-653">Carmen</h4>
                        <p className="text-sm text-gray-400" data-id="element-654">
                          Channel / Manager
                        </p>
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-655">
                      <h5 className="text-sm font-semibold text-white mb-2 uppercase tracking-wider" data-id="element-656">
                        Problem Statement
                      </h5>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-657">
                        Carmen is a senior manager for the BuSS who needs a
                        better way to manage pricing and discounts across
                        catalogs and bundles.
                      </p>
                    </div>
                    <div data-id="element-658">
                      <h5 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider" data-id="element-659">
                        Challenges
                      </h5>
                      <ul className="space-y-2 text-sm text-gray-400" data-id="element-660">
                        <li className="flex items-start gap-2" data-id="element-661">
                          <span className="text-red-400 mt-1" data-id="element-662">•</span>
                          <span data-id="element-663">
                            All discounts are handled manually, no rule-based
                            opportunities
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-664">
                          <span className="text-red-400 mt-1" data-id="element-665">•</span>
                          <span data-id="element-666">
                            Takes 6-12 months to add partners to the platform
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-667">
                          <span className="text-red-400 mt-1" data-id="element-668">•</span>
                          <span data-id="element-669">No way to group packages for sales</span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-670">
                          <span className="text-red-400 mt-1" data-id="element-671">•</span>
                          <span data-id="element-672">Partner APIs frequently fail and outdated</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Description */}
              <div className="mb-16" data-id="element-673">
                <p className="text-lg text-gray-400 leading-relaxed max-w-4xl" data-id="element-674">
                  Through the course of developing these Personas, we identified
                  key workflows that any redesigned system would need to
                  address. Among them: On-boarding new vendors, products, and
                  product bundles. Educating salespeople on the products that
                  they're expected to sell. Giving salespeople access to tools
                  to help them optimize sales opportunities. Building and
                  tracking quotes.
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-12 items-center mb-16" data-id="element-675">
                <div className="flex-1" data-id="element-676">
                  <h3 className="text-2xl font-bold mb-4" data-id="element-677">
                    Reimagining the Sales Experience
                  </h3>
                  <p className="text-gray-400 text-lg leading-relaxed mb-6" data-id="element-678">
                    We envisioned a unified platform that would transform bulk
                    quote building from a tedious manual process into an
                    intelligent, guided workflow. The key was balancing power
                    and simplicity - giving sales reps advanced capabilities
                    without overwhelming complexity.
                  </p>
                  <ul className="space-y-4" data-id="element-679">
                    <li className="flex items-start gap-3" data-id="element-680">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-681" />
                      <span className="text-gray-300" data-id="element-682">
                        Smart catalog search with ML-powered recommendations
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-683">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-684" />
                      <span className="text-gray-300" data-id="element-685">
                        Bulk operations with intelligent validation
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-686">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-687" />
                      <span className="text-gray-300" data-id="element-688">
                        Real-time pricing calculations and margin visibility
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-689">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-690" />
                      <span className="text-gray-300" data-id="element-691">
                        Centralized catalog management with version control
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="flex-1 w-full" data-id="element-692">
                  <div className="bg-zinc-800 rounded-2xl border border-white/10 aspect-square flex items-center relative overflow-hidden" data-id="element-693">
                    <img src="https://i.imgur.com/IrTGuJ7.jpeg" alt="Stakeholder Interview Notes and Affinity Mapping" className="w-full h-full object-cover rounded-xl" data-id="element-694" />
                  </div>
                </div>
              </div>

              {/* Impact/Effort Matrix */}
              <div className="mt-20" data-id="element-695">
                <h3 className="text-2xl font-bold text-white mb-6" data-id="element-696">
                  Impact/Effort Matrix
                </h3>
                <p className="text-lg text-gray-400 leading-relaxed mb-8 max-w-4xl" data-id="element-697">
                  We chose to do the impact/effort matrix because this diagram
                  provides many insights in just one exercise. From this diagram
                  we can extrapolate which features provide the most value to
                  the user which tells us how prevalent those features should be
                  in our designs and especially our dashboards.
                </p>
                <ImpactEffortMatrix data-id="element-698" />
              </div>
            </CaseStudySection>

            {/* Exploration & Iteration */}
            <CaseStudySection id="exploration" title="Exploration & Iteration" data-id="element-699">
              <p className="text-xl text-gray-400 max-w-3xl mb-12" data-id="element-700">
                Through multiple rounds of wireframing and prototyping, we
                tested different approaches to the quote builder interface,
                focusing on reducing cognitive load while maintaining power user
                capabilities.
              </p>

              {/* Wireframes */}
              <div className="space-y-8 mb-16" data-id="element-701">
                <div className="bg-zinc-900 rounded-3xl overflow-hidden border border-white/10" data-id="element-702">
                  <img src="https://cdn.magicpatterns.com/uploads/8fDhEMYkxs7PCDW5qF1WXA/wireframe_01.jpg" alt="Navigation and global structure wireframe" className="w-full h-auto" data-id="element-703" />
                  <div className="p-6 bg-black/40" data-id="element-704">
                    <h4 className="text-lg font-semibold text-white mb-2" data-id="element-705">
                      Navigation & Global Structure
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-706">
                      Dynamic dashboard with modular navigation empowering users
                      to follow any path. Cart-based quote building leverages
                      familiar e-commerce patterns.
                    </p>
                  </div>
                </div>

                <div className="bg-zinc-900 rounded-3xl overflow-hidden border border-white/10" data-id="element-707">
                  <img src="https://cdn.magicpatterns.com/uploads/giXENBMU4RZFnJGGU2cYXy/wireframe_02.jpg" alt="Quote management and customer interface wireframe" className="w-full h-auto" data-id="element-708" />
                  <div className="p-6 bg-black/40" data-id="element-709">
                    <h4 className="text-lg font-semibold text-white mb-2" data-id="element-710">
                      Quote Management & Customer Interface
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-711">
                      Persistent summary bar provides quick access to active
                      quotes. Customer search with quote history enables
                      efficient quote management.
                    </p>
                  </div>
                </div>

                <div className="bg-zinc-900 rounded-3xl overflow-hidden border border-white/10" data-id="element-712">
                  <img src="https://cdn.magicpatterns.com/uploads/pAqXeuaEvYg6QHP47B4LXo/wireframe_03.jpg" alt="Quote status organization and workflow wireframe" className="w-full h-auto" data-id="element-713" />
                  <div className="p-6 bg-black/40" data-id="element-714">
                    <h4 className="text-lg font-semibold text-white mb-2" data-id="element-715">
                      Quote Status & Workflow Organization
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-716">
                      Process-based organization puts quotes in context of their
                      state. List and grid views provide flexible searching.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-717">
                <div className="space-y-4" data-id="element-718">
                  <div className="bg-zinc-900 rounded-2xl border border-white/10 aspect-[4/3] flex items-center justify-center relative" data-id="element-719">
                    <ShoppingCart className="w-12 h-12 text-white/20" data-id="element-720" />
                    <span className="absolute bottom-4 left-4 text-xs text-white/40 uppercase tracking-wider" data-id="element-721">
                      Cart-Based Approach
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm" data-id="element-722">
                    Familiar e-commerce pattern but felt too consumer-focused
                    for enterprise sales.
                  </p>
                </div>
                <div className="space-y-4" data-id="element-723">
                  <div className="bg-zinc-900 rounded-2xl border border-white/10 aspect-[4/3] flex items-center justify-center relative" data-id="element-724">
                    <Database className="w-12 h-12 text-white/20" data-id="element-725" />
                    <span className="absolute bottom-4 left-4 text-xs text-white/40 uppercase tracking-wider" data-id="element-726">
                      Spreadsheet View
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm" data-id="element-727">
                    Power users loved it but new reps found it overwhelming and
                    error-prone.
                  </p>
                </div>
                <div className="space-y-4" data-id="element-728">
                  <div className="bg-zinc-900 rounded-2xl border border-white/10 aspect-[4/3] flex items-center justify-center relative" data-id="element-729">
                    <Layers className="w-12 h-12 text-white/20" data-id="element-730" />
                    <span className="absolute bottom-4 left-4 text-xs text-white/40 uppercase tracking-wider" data-id="element-731">
                      Guided Builder
                    </span>
                  </div>
                  <p className="text-green-400 text-sm" data-id="element-732">
                    Winner: Step-by-step guidance with advanced mode toggle for
                    power users.
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-2xl p-8 border border-white/10" data-id="element-733">
                <h4 className="text-xl font-bold mb-4" data-id="element-734">Key Design Decisions</h4>
                <div className="grid md:grid-cols-2 gap-6 text-gray-400" data-id="element-735">
                  <div data-id="element-736">
                    <h5 className="text-white font-semibold mb-2" data-id="element-737">
                      Progressive Disclosure
                    </h5>
                    <p className="text-sm" data-id="element-738">
                      Advanced features hidden by default but easily accessible
                      for power users through keyboard shortcuts and toggle
                      switches.
                    </p>
                  </div>
                  <div data-id="element-739">
                    <h5 className="text-white font-semibold mb-2" data-id="element-740">
                      Intelligent Validation
                    </h5>
                    <p className="text-sm" data-id="element-741">
                      Real-time error checking with contextual help prevents
                      mistakes before they happen.
                    </p>
                  </div>
                </div>
              </div>
            </CaseStudySection>

            {/* The Solution */}
            <CaseStudySection id="solution" title="The Design Solution" darker data-id="element-742">
              <div className="mb-16 max-w-3xl mx-auto" data-id="element-743">
                <p className="text-lg text-gray-300" data-id="element-744">
                  The final design creates a seamless experience that guides new
                  users while empowering experienced reps. Smart defaults, bulk
                  operations, and real-time validation transform quote building
                  from hours to minutes.
                </p>
              </div>

              <DesignShowcase data-id="element-745" />

              <div className="grid md:grid-cols-3 gap-8 mt-16 mb-16" data-id="element-746">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-747">
                  <ShoppingCart className="w-10 h-10 text-blue-400 mb-4" data-id="element-748" />
                  <h4 className="text-lg font-bold mb-2" data-id="element-749">Quote Builder</h4>
                  <p className="text-gray-400 text-sm" data-id="element-750">
                    Intelligent product search, bulk add capabilities, and
                    real-time pricing with margin calculations.
                  </p>
                </div>
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-751">
                  <Database className="w-10 h-10 text-purple-400 mb-4" data-id="element-752" />
                  <h4 className="text-lg font-bold mb-2" data-id="element-753">Catalog Manager</h4>
                  <p className="text-gray-400 text-sm" data-id="element-754">
                    Centralized product database with version control, regional
                    pricing, and bulk update tools.
                  </p>
                </div>
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-755">
                  <BarChart3 className="w-10 h-10 text-green-400 mb-4" data-id="element-756" />
                  <h4 className="text-lg font-bold mb-2" data-id="element-757">
                    Analytics Dashboard
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-758">
                    Sales performance metrics, quote conversion tracking, and
                    product popularity insights.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16" data-id="element-759">
                <div className="text-center" data-id="element-760">
                  <div className="text-4xl font-bold text-white mb-2" data-id="element-761">75%</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-762">
                    Time Reduction
                  </div>
                </div>
                <div className="text-center" data-id="element-763">
                  <div className="text-4xl font-bold text-white mb-2" data-id="element-764">90%</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-765">
                    Error Reduction
                  </div>
                </div>
                <div className="text-center" data-id="element-766">
                  <div className="text-4xl font-bold text-white mb-2" data-id="element-767">
                    $2.4M
                  </div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-768">
                    Annual Savings
                  </div>
                </div>
                <div className="text-center" data-id="element-769">
                  <div className="text-4xl font-bold text-white mb-2" data-id="element-770">95%</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-771">
                    Correct Qoute Delivery
                  </div>
                </div>
              </div>
            </CaseStudySection>

            {/* Conclusion */}
            <CaseStudySection data-id="element-772">
              <div className="max-w-3xl mx-auto" data-id="element-773">
                <h2 className="text-3xl md:text-5xl font-light tracking-[0.3em] text-white mb-8" data-id="element-774">
                  Reflection & Impact
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-12" data-id="element-775">
                  This project demonstrated the power of deep user research and
                  iterative design. By understanding the real workflows and pain
                  points of sales teams, we created a solution that didn't just
                  digitize existing processes - it fundamentally reimagined how
                  bulk sales could work. The success of this engagement
                  established a long-term partnership with Verizon for future
                  enterprise tool design.
                </p>
              </div>
            </CaseStudySection>
          </div>
        </div>

        {/* Bottom Navigation */}
        <nav className="border-t border-white/10 bg-zinc-950 py-16 px-6" data-id="element-776">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-8" data-id="element-777">
            <Link to="/" className="group flex items-center gap-4 text-left" data-id="element-778">
              <div className="p-4 rounded-full border border-white/10 bg-white/5 group-hover:bg-white group-hover:text-black transition-all" data-id="element-779">
                <ArrowLeft className="w-6 h-6" data-id="element-780" />
              </div>
              <div data-id="element-781">
                <span className="block text-sm text-gray-500 uppercase tracking-wider mb-1" data-id="element-782">
                  Back to
                </span>
                <span className="text-xl font-bold text-white group-hover:text-gray-300 transition-colors" data-id="element-783">
                  Home
                </span>
              </div>
            </Link>
            <Link to="/dashboard" className="group flex items-center gap-4 text-right flex-row-reverse md:flex-row" data-id="element-784">
              <div className="text-right" data-id="element-785">
                <span className="block text-sm text-gray-500 uppercase tracking-wider mb-1" data-id="element-786">
                  Next Project
                </span>
                <span className="text-xl font-bold text-white group-hover:text-gray-300 transition-colors" data-id="element-787">
                  From Chaos to Clarity
                </span>
              </div>
              <div className="p-4 rounded-full border border-white/10 bg-white/5 group-hover:bg-white group-hover:text-black transition-all" data-id="element-788">
                <ArrowRight className="w-6 h-6" data-id="element-789" />
              </div>
            </Link>
          </div>
        </nav>

        {/* Footer */}
        <footer className="py-12 text-center text-gray-600 text-sm border-t border-white/10" data-id="element-790">
          <p data-id="element-791">© {new Date().getFullYear()} Taylor Sams. All rights reserved.</p>
        </footer>
      </main>
    </div>;
}